import { useEffect, useState } from 'react';
import { Button, useToast } from '@vd/ui';
import { Minus, Plus, AlertTriangle } from 'lucide-react';
import { api } from '../../../lib/api';
import { formatDay } from '../../../lib/time';

/**
 * Inline seat-quantity adjustment panel.
 *
 * Used in two places:
 *   1. The invite modal's over-cap state: user has typed N emails,
 *      needs (N - seatsAvailable) more seats. Default quantity is the
 *      minimum needed; user can bump higher to buy buffer for future
 *      invites without coming back.
 *   2. The members tab's seat-management area: owner wants to
 *      reduce the number of paid seats after removing members.
 *
 * Two API calls:
 *   - previewSeatChange returns the proration amount (positive =
 *     charged today, negative = credited to next invoice). Auto-fires
 *     after a debounce when the user changes the quantity.
 *   - applySeatChange commits the change. The Stripe subscription is
 *     updated and the customer.subscription.updated webhook will
 *     update companies.seatsPurchased — the parent component is
 *     responsible for refetching seat data after onApplied fires.
 *
 * The panel does NOT preserve any external state (e.g. invite-modal
 * email textarea); the caller keeps its state mounted while this
 * panel is rendered, and refetches when onApplied is called.
 */

export interface AddSeatsPanelProps {
  companyId: string;
  /** The team's currently-paid seat count. Drives the +/- minimum. */
  currentQuantity: number;
  /**
   * The minimum quantity allowed. The API also enforces a hard floor
   * (currentMembers + openInvites) but keeping the floor here lets
   * the +/- input clamp without a server round-trip.
   */
  minQuantity: number;
  /**
   * The default quantity to show when the panel opens. Typically:
   *   - From the invite modal: max(currentQuantity, seatsNeeded)
   *   - From the members tab decrease flow: minQuantity
   */
  defaultQuantity: number;
  /** Optional caption above the +/- control. Tailors the copy. */
  intentLabel?: string;
  /** Fires after a successful applySeatChange. Caller refetches seats. */
  onApplied: () => void;
  /** Fires when the user clicks Cancel. Caller closes the panel. */
  onCancel: () => void;
}

export function AddSeatsPanel({
  companyId,
  currentQuantity,
  minQuantity,
  defaultQuantity,
  intentLabel,
  onApplied,
  onCancel,
}: AddSeatsPanelProps) {
  const toast = useToast();
  const [quantity, setQuantity] = useState(defaultQuantity);
  const [preview, setPreview] = useState<{
    prorationAmount: number;
    currency: string;
    nextBillDate: string | null;
  } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  // Debounce the preview call: user can spam +/- and we don't want to
  // hammer Stripe. 400ms is the sweet spot — fast enough that the user
  // sees the price update before they decide to click confirm.
  useEffect(() => {
    if (quantity === currentQuantity) {
      setPreview({ prorationAmount: 0, currency: 'usd', nextBillDate: null });
      setPreviewError(null);
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    const timer = setTimeout(async () => {
      try {
        const r = await api.previewSeatChange(companyId, quantity);
        setPreview({
          prorationAmount: r.prorationAmount,
          currency: r.currency,
          nextBillDate: r.nextBillDate,
        });
      } catch (err) {
        const e = err as Error & { code?: string };
        setPreview(null);
        setPreviewError(e.message || 'Could not preview the price.');
      } finally {
        setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [companyId, quantity, currentQuantity]);

  const onConfirm = async () => {
    if (applying) return;
    setApplying(true);
    try {
      await api.applySeatChange(companyId, quantity);
      toast.push({
        variant: 'success',
        title: quantity > currentQuantity ? 'Seats added' : 'Seats reduced',
        description: `Now paying for ${quantity} seat${quantity === 1 ? '' : 's'}.`,
      });
      onApplied();
    } catch (err) {
      const e = err as Error & { code?: string };
      toast.push({
        variant: 'danger',
        title: 'Could not change seats',
        description: e.message || 'Try again or use Manage billing.',
      });
    } finally {
      setApplying(false);
    }
  };

  const isIncrease = quantity > currentQuantity;
  const isDecrease = quantity < currentQuantity;
  const isUnchanged = quantity === currentQuantity;

  return (
    <div style={shellStyle}>
      {intentLabel && (
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>{intentLabel}</div>
      )}
      <div style={rowStyle}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setQuantity(Math.max(minQuantity, quantity - 1))}
          disabled={quantity <= minQuantity || applying}
          aria-label="Decrease seats"
        >
          <Minus size={14} />
        </Button>
        <div style={countStyle}>
          <strong>{quantity}</strong> seat{quantity === 1 ? '' : 's'}
          <span style={{ marginLeft: 8, color: 'var(--text-tertiary)', fontSize: 12 }}>
            (currently {currentQuantity})
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setQuantity(quantity + 1)}
          disabled={applying}
          aria-label="Increase seats"
        >
          <Plus size={14} />
        </Button>
      </div>

      <div style={previewBoxStyle}>
        {previewLoading && <span style={{ color: 'var(--text-tertiary)' }}>Calculating prorated cost…</span>}
        {!previewLoading && previewError && (
          <span style={{ color: 'var(--danger)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={13} /> {previewError}
          </span>
        )}
        {!previewLoading && !previewError && preview && (
          <PreviewSummary
            prorationAmount={preview.prorationAmount}
            currency={preview.currency}
            nextBillDate={preview.nextBillDate}
            isIncrease={isIncrease}
            isDecrease={isDecrease}
            isUnchanged={isUnchanged}
          />
        )}
      </div>

      <div style={actionsStyle}>
        <Button variant="ghost" onClick={onCancel} disabled={applying}>Cancel</Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          disabled={applying || previewLoading || isUnchanged || !!previewError}
        >
          {applying ? 'Applying…' : isDecrease ? 'Confirm and reduce seats' : 'Confirm and add seats'}
        </Button>
      </div>
    </div>
  );
}

function PreviewSummary({
  prorationAmount,
  currency,
  nextBillDate,
  isIncrease,
  isDecrease,
  isUnchanged,
}: {
  prorationAmount: number;
  currency: string;
  nextBillDate: string | null;
  isIncrease: boolean;
  isDecrease: boolean;
  isUnchanged: boolean;
}) {
  if (isUnchanged) {
    return <span style={{ color: 'var(--text-tertiary)' }}>No change. Pick a different seat count to see the price.</span>;
  }
  const formatted = formatMoney(Math.abs(prorationAmount), currency);
  // Stripe returns proration as positive when the change is a charge
  // (increase mid-period) and negative when it's a credit (decrease
  // mid-period). The UI flips between "charged" and "credited" copy
  // for clarity — same number, opposite sign of impact on the card.
  if (isIncrease) {
    return (
      <span>
        <strong style={{ color: 'var(--text)' }}>{formatted}</strong> will be charged to your card today, prorated to your current billing period.
        {nextBillDate && (
          <span style={{ color: 'var(--text-tertiary)' }}> Next bill on {formatDate(nextBillDate)}.</span>
        )}
      </span>
    );
  }
  if (isDecrease) {
    return (
      <span>
        <strong style={{ color: 'var(--text)' }}>{formatted}</strong> will be credited to your account, applied automatically to your next invoice.
        {nextBillDate && (
          <span style={{ color: 'var(--text-tertiary)' }}> Next bill on {formatDate(nextBillDate)}.</span>
        )}
      </span>
    );
  }
  return null;
}

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return formatDay(iso);
}

const shellStyle: React.CSSProperties = {
  padding: 12,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg-muted)',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '6px 0',
};

const countStyle: React.CSSProperties = {
  flex: 1,
  fontSize: 14,
  color: 'var(--text)',
};

const previewBoxStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
  minHeight: 20,
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
};
